import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { formatRoutingNumberForServer } from '@razz-team/utils';
import { ApiNewBeneficiary } from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { useMemo } from 'react';
import { mapResponse } from './helpers';

export function useBeneficiariesQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['beneficiaries'],
    queryFn: ({ signal }) =>
      client
        .GET('/beneficiaries', { signal })
        .then(mapResponse)
        .then((it) => it.beneficiaries),
  });
}

function clearRoutingNumber(value: string) {
  return formatRoutingNumberForServer(value).replace(/XXX$/, '');
}

export function useBankInfo(code: string | null | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['banks', code],
    queryFn: ({ signal }) =>
      client
        .GET('/banks/{code}', {
          signal,
          params: {
            path: { code: clearRoutingNumber(code!) },
          },
        })
        .then(mapResponse),
    enabled: Boolean(code),
  });
}

export function useValidateBeneficiary(body: ApiNewBeneficiary | null | undefined) {
  const client = useApiClient();
  const enabled = useMemo(() => {
    if (!body) return false;
    if (!body.currency) return false;
    if (!body.accountNumber) return false;
    if (body.bankCountry) {
      if (!(body.individualName || body.businessName)) return false;
    }
    return true;
  }, [body]);

  return useQuery({
    enabled,
    queryKey: ['beneficiaries', 'validate', body],
    queryFn: ({ signal }) =>
      client
        .POST('/beneficiaries/validate', {
          signal,
          body: body!,
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
  });
}
